import React from 'react';
import PropTypes from "prop-types";
import polyline from '@mapbox/polyline';
import * as turf from '@turf/turf';

const fillColor = '#5bce61';
const borderColor = '#4aa84f';
const middleColor = '#4eb153';

const routePropType = PropTypes.shape({
  dynamic: PropTypes.shape({
    visibility: PropTypes.bool,
    polylines: PropTypes.arrayOf(PropTypes.string),
  }),
  static: PropTypes.shape({
    visibility: PropTypes.bool,
    polylines: PropTypes.arrayOf(PropTypes.string),
  }),
});

export default class Polyline extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      polylines: [],
    };
    this.existingPolylines = {};
  }

  static propTypes = {
    route: routePropType,
    driverLocation: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
    snappedSegment: PropTypes.arrayOf(PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    })),
    map: PropTypes.object.isRequired,
  };

  clearExistingPolyline() {
    const map = this.props.map;
    this.state.polylines.forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
        map.removeSource(layerId);
      }
    });
    this.setState({ polylines: [] });
  }

  getDriverLocToDestCoordinates(encodedPath, driverLocation, snappedSegment) {
    const polylineCoordinates = polyline.decode(encodedPath).map(coord => [coord[1], coord[0]]);
    if (!snappedSegment) {
      return polylineCoordinates;
    }

    const [segmentStart, segmentEnd] = snappedSegment.map(coord => [coord.longitude, coord.latitude]);
    const currentSegmentIndex = polylineCoordinates.findIndex((coord, index) =>
      turf.distance(turf.point(coord), turf.point(segmentStart)) <= 0.001 &&
      (index + 1 < polylineCoordinates.length ? turf.distance(turf.point(polylineCoordinates[index + 1]), turf.point(segmentEnd)) <= 0.001 : true)
    );

    const driverLoc = [driverLocation.longitude, driverLocation.latitude];
    return [driverLoc, ...polylineCoordinates.slice(currentSegmentIndex + 1)];
  }

  updateOrAddPolylineToMap(layerId, coordinates, color, width) {
    const map = this.props.map;

    if (map.getSource(layerId)) {
      map.getSource(layerId).setData({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: coordinates,
        },
      });
    } else {
      map.addSource(layerId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: coordinates,
          },
        },
      });

      map.addLayer({
        id: layerId,
        type: 'line',
        source: layerId,
        paint: {
          'line-color': color,
          'line-width': width,
        },
      });
    }
  }

  getDynamicPolyline() {
    if (this.props.route.dynamic && this.props.route.dynamic.visibility && this.props.driverLocation) {
      const encodedPath = this.props.route.dynamic.polylines[0];
      return this.getDriverLocToDestCoordinates(encodedPath, this.props.driverLocation, this.props.snappedSegment);
    }
    return null;
  }

  getStaticPolylines() {
    if (this.props.route.static && this.props.route.static.visibility) {
      return this.props.route.static.polylines;
    }
    return [];
  }

  updatePolylineLayers() {
    const dynamicPolyline = this.getDynamicPolyline();
    if (dynamicPolyline) {
      const dynamicLayerId = 'dynamic-polyline';
      this.updateOrAddPolylineToMap(dynamicLayerId + "-border", dynamicPolyline, borderColor, 4);
      this.updateOrAddPolylineToMap(dynamicLayerId + "-middle", dynamicPolyline, middleColor, 3);
      this.updateOrAddPolylineToMap(dynamicLayerId + "-fill", dynamicPolyline, fillColor, 2);
      this.setState({ polylines: [`${dynamicLayerId}-border`, `${dynamicLayerId}-middle`, `${dynamicLayerId}-fill`] });
    } else {
      this.clearExistingPolyline();
    }

    const staticPolylines = this.getStaticPolylines();
    staticPolylines.forEach((encodedPath, index) => {
      const polylineCoordinates = polyline.decode(encodedPath).map(coord => [coord[1], coord[0]]);
      const staticLayerId = `static-polyline-${index}`;
      this.updateOrAddPolylineToMap(staticLayerId  + "-border", polylineCoordinates, borderColor, 4);
      this.updateOrAddPolylineToMap(staticLayerId  + "-middle", polylineCoordinates, middleColor, 3);
      this.updateOrAddPolylineToMap(staticLayerId  + "-fill", polylineCoordinates, fillColor, 2);
      this.setState(prevState => ({
        polylines: [...prevState.polylines, `${staticLayerId}-border`, `${staticLayerId}-middle`, `${staticLayerId}-fill`],
      }));
    });
  }

  componentDidMount() {
    this.updatePolylineLayers();
  }

  componentDidUpdate(prevProps) {
    if(this.props.driverLocation == null) return
    const routeChanged = this.checkIfRouteChanged(this.props.route, prevProps.route);
    const locationChanged = this.checkIfVehicleLocationChanged(this.props.driverLocation, prevProps.driverLocation);

    if (routeChanged || locationChanged) {
      this.updatePolylineLayers();
    }
  }

  componentWillUnmount() {
    this.clearExistingPolyline();
  }

  checkIfRouteChanged(newRoute, oldRoute) {
    return JSON.stringify(newRoute) !== JSON.stringify(oldRoute);
  }

  checkIfVehicleLocationChanged(newLocation, oldLocation) {
    return newLocation.latitude !== oldLocation.latitude || newLocation.longitude !== oldLocation.longitude;
  }

  render() {
    return null;
  }
}
